
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {MenuItem, SvgIcon, Typography, useTheme} from '@mui/material';
import {styled} from '@mui/system';
import {
  Box,
  Stack,
  Divider,
} from '@zoom/react-ui';
import {
  IconChevronDownFill,
  IconChevronUpFill,
  IconChevronRightSmall,
  IconMoreAction,
  IconPopWindowPopIn,
  IconQuestionCircle,
  IconSetting,
  IconShare,
} from '@zoom/sun-ui-icon/dist/cjs/index.js';
import {useTranslation} from 'react-i18next';
import {
  useSelector,
} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {ShepherdTourContext} from 'react-shepherd';

import HostContext from '../../HostContext';
import {AccountConfirmPopup} from '../AccountConfirmPopup/AccountConfirmPopup';

import {ReactComponent as AlertIcon} from 'assets/svg/alert-filled.svg';
import {ReactComponent as CopyOutlined} from 'assets/svg/copy-outlined.svg';
import {ReactComponent as GoogleIcon} from 'assets/svg/google-icon-avatar.svg';
import {ReactComponent as ICloudIcon} from 'assets/svg/icloud.svg';
import {ReactComponent as InfoIcon} from 'assets/svg/info-icon.svg';
import {ReactComponent as MSIcon} from 'assets/svg/ms-icon-avatar.svg';
import {ReactComponent as ZoomIcon} from 'assets/svg/zoom-icon-avatar.svg';
import {CustomMenuItem} from 'Components/AppointmentsList/Style';
import {InfoField} from 'Components/InfoField';
import {accountAccessTokenInvalid} from 'Components/Integration/AccountSettings';
import {CustomLinkPopup} from 'Components/NewFeaturePopup/CustomLinkPopup';
import {StyledLink} from 'Components/OrganizerLandingPage/Style';
import {ZmQuinaryBtn, ZmTertiaryBtn} from 'Components/ZmButtons';
import {ZmMenu} from 'Components/ZmDialogs';
import {ZmPrimaryLink} from 'Components/ZmLink/ZmLink';
import {ZmTooltip} from 'Components/ZmTooltip/ZmTooltip';
import {ARTICLES_LINK, FAQ_LINK, PROVIDER_TYPE, VIDEO_TUTORIAL_LINK} from 'Utils/consts';
import {copyTextToClipboard, getBGColorByName, isZoomieEmail} from 'Utils/index.js';
import {inZoomClient, openFeedbackDialog} from 'Utils/integration';
import {openUrlInBrowser, WEB_SCHEDULER_URL} from 'Utils/ZoomClientUtils';
import zoomPalette from 'zoomPalette';

const dropDownBtnOptions = [
  {
    name: 'supportLinks.videoTutorial',
    to: VIDEO_TUTORIAL_LINK,
  },
  {
    name: 'supportLinks.faq',
    to: FAQ_LINK,
  },
  {
    name: 'supportLinks.articles',
    to: ARTICLES_LINK,
  },
];

export const AvatarContainer = styled(Box)(({theme}) => ({
  height: '80px',
  width: '80px',
  borderRadius: '26px',
  backgroundColor: theme.palette.error.swatch['30'],
  color: theme.palette.common.white,
  fontSize: '36px',
  lineHeight: '80px',
  fontWeight: 500,
  textAlign: 'center',
  flexShrink: '0',
  marginRight: '16px',
  textTransform: 'uppercase',
}));

export const TextBtn = styled('span')(({theme}) => ({
  fontWeight: 450,
  fontSize: '14px',
  color: theme.palette.primary.main,
  cursor: 'pointer',
}));

const CustomMenu = styled(ZmMenu)(() => ({
  '.MuiMenu-paper': {
    padding: '4px',
    minWidth: '254px',
  },
  '.MuiMenuItem-root': {
    'padding': '4px',
    'borderRadius': '4px',

    '&.Mui-focusVisible': {
      backgroundColor: zoomPalette.white,
    },
  },

}));

/**
 * @param {Object} props
 * @param {String} props.displayName
 * @return {ReactElement}
 */
export default function Avatar(props) {
  const {account, avatarStyle, displayName, onSharePublicPage, showSettings} = props;
  let {boxStyle, linkHref, nameStyle} = props;
  const {t} = useTranslation();

  const [isCopied, setIsCopied] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  // const {goToAuth} = useConnect3rdParty();

  const anchorEl = useRef(null);
  const anchorRefLearnMore = useRef(null);

  const accounts = useSelector((state) => state.integrationState.accounts.value);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);

  useEffect(() => {
    if (accounts.length > 0) {
      accounts.forEach((account) =>{
        // if one of the zoomie accounts is zmail and is the curently bind account
        // and showAddAccountPopup in local storage is not set
        // display popup with option to switch accounts
        if (isZoomieEmail(account.email) && account.service === PROVIDER_TYPE.ZOOM && account.pushEnabled) {
          if (localStorage.getItem('showAddAccountPopup') === null) {
            setDropdownOpen(true);
          };
        }
      });
      const boundEmail = accounts?.find((account) => account.pushEnabled);
      if (!boundEmail) {
        setDropdownOpen(true);
      }
    }
  }, [accounts]);

  if (!boxStyle) {
    boxStyle = {marginBottom: '40px', width: '305px'};
  }
  if (!nameStyle) {
    nameStyle = {fontSize: '1.25rem', lineHeight: '1.5rem', fontWeight: '500'};
  }
  nameStyle.color = theme.palette.text.primary;

  const name = displayName?.split(' ') || [''];

  const {APPLE, GOOGLE_CALENDAR, OUTLOOK_CALENDAR, ZOOM} = PROVIDER_TYPE;
  const iconMap = {
    [GOOGLE_CALENDAR]: GoogleIcon,
    [ZOOM]: ZoomIcon,
    [OUTLOOK_CALENDAR]: MSIcon,
    [APPLE]: ICloudIcon,
  };

  const hostValue = useContext(HostContext);

  const profilePicture = useSelector((s) =>
    hostValue?.isHost ?
      s.hostSettingsState.profilePicture?.value?.data ?? '' :
      s.bookAppointments.profilePicture?.value?.data ?? ''
  );

  const handleOpenDropdown = () => !!account && setDropdownOpen(true);
  const handleCloseDropdown = () => setDropdownOpen(false);

  const handleCloseLearnMore = () => setLearnMoreOpen(false);
  const handleOpenLearnMore = () => setLearnMoreOpen(true);

  const handleOnSetting = () => {
    openUrlInBrowser(`${WEB_SCHEDULER_URL}?action=settings-profile`);
  };
  const tour = useContext(ShepherdTourContext);

  const walkthrough = useSelector((s) => s.hostSettingsState.walkthrough);
  const enableFeedback = useSelector((state) => state.integrationState.enableFeedback);

  return (
    <Box sx={boxStyle}>
      <Box ref={anchorEl} onClick={handleOpenDropdown}>
        <InfoField sx={{marginTop: 0, overflowWrap: 'anywhere'}}>
          {
          profilePicture !== '' ?
          <img
            style={{
              height: '80px',
              width: '80px',
              borderRadius: '26px',
              flexShrink: '0',
              marginRight: '16px',
              ...avatarStyle,
            }}
            src={`data:image/*;base64,${profilePicture}`}
            alt="profile"/> :
          <AvatarContainer
            sx={{...avatarStyle, backgroundColor: getBGColorByName(name)}}>
            {name[0][0] || ''}{name[1]?.[0] || ''}
          </AvatarContainer>
          }
          <Stack direction="column">
            <Typography sx={nameStyle}>
              {name.join(' ')}
            </Typography>
            {linkHref && (
              <Stack direction="row" alignItems="center" gap="4px" flexWrap="wrap">
                <CustomLinkPopup>
                  <>
                    <ZmPrimaryLink
                      underline="hover"
                      onClick={(e) => {
                        e.stopPropagation();
                        openUrlInBrowser(linkHref);
                        if (walkthrough && tour?.isActive()) {
                          tour.complete();
                        }
                      }}
                      className={'fifth-step'}
                    >
                      <ZmTooltip title={t('landing.viewPublicPageTooltip')}>
                        <Typography
                          sx={{fontSize: '.875rem', fontWeight: 500}}
                        >
                          {t('common.viewPublicPage')}
                        </Typography>
                      </ZmTooltip>
                    </ZmPrimaryLink>
                  </>
                </CustomLinkPopup>
                <ZmTooltip
                  title={t(isCopied? 'common.copied': 'common.copy')}
                  leaveDelay={200}
                  TransitionProps={{
                    onExited: () => setIsCopied(false),
                  }}
                >
                  <ZmQuinaryBtn
                    size="xsmall"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyTextToClipboard(linkHref).then(() => {
                        setIsCopied(true);
                      });
                    }}
                    sx={{
                      padding: '5px',
                    }}
                  >
                    <SvgIcon sx={{margin: 'auto !important'}}
                      fontSize="inherit" component={CopyOutlined} inheritViewBox />
                  </ZmQuinaryBtn>
                </ZmTooltip>
                <ZmTertiaryBtn
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSharePublicPage();
                  }}
                  sx={{marginLeft: '4px', padding: '8px'}}
                >
                  <IconShare style={{width: '22px'}}/> {t('common.share')}
                </ZmTertiaryBtn>
                {!!account && dropdownOpen ? <IconChevronUpFill marginLeft="13px" /> :
                  <IconChevronDownFill marginLeft="13px" />}
              </Stack>
            )}
          </Stack>
        </InfoField>
      </Box>
      <CustomMenu
        open={dropdownOpen}
        anchorEl={anchorEl.current}
        onClose={handleCloseDropdown}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {account ? (
          <AccountConfirmPopup>
            <Stack direction="row" alignItems="center" flexWrap="wrap" padding="4px">
              <SvgIcon
                style={{width: '16px', height: '16px', margin: 0}}
                component={iconMap[account.service]}
                inheritViewBox
              />

              <Typography sx={{
                fontWeight: 500, fontSize: '14px', lineHeight: '20px', margin: '0 4px 0 8px'}}>{account.email}
              </Typography>

              <ZmTooltip
                title={t(account.blocked? 'integration.accountErrorTips': 'landing.infoAccount')}
                arrow
              >
                <SvgIcon
                  style={{width: '14px', height: '14px', marginRight: '8px'}}
                  component={account.blocked? AlertIcon: InfoIcon}
                  inheritViewBox
                />
              </ZmTooltip>
              {
                account.blocked && accountAccessTokenInvalid(account) && <TextBtn onClick={() => {
                  if (inZoomClient) {
                    openUrlInBrowser(`${WEB_SCHEDULER_URL}?action=settings-account`);
                  } else {
                    // Navigate user to account settings page
                    navigate('/me/settings/account');
                    // goToAuth(account.service);
                  }
                }}>{t('integration.reAuthorize')}</TextBtn>
              }
            </Stack>
          </AccountConfirmPopup>) :
          (<AccountConfirmPopup />)
        }
        <Divider sx={{margin: '8px 0'}} />
        {showSettings && <MenuItem sx={{gap: '8px'}} onClick={handleOnSetting}>
          <IconSetting />
          <Typography sx={{flexGrow: 1, fontSize: '14px'}}>{t('landing.settings')}</Typography>
          <IconPopWindowPopIn />
        </MenuItem>}
        <MenuItem sx={{gap: '8px'}} ref={anchorRefLearnMore} onClick={handleOpenLearnMore}>
          <IconQuestionCircle />
          <Typography sx={{flexGrow: 1, fontSize: '14px'}}>{t('common.learnMore')}</Typography>
          <IconChevronRightSmall />
        </MenuItem>
        {enableFeedback && <MenuItem sx={{gap: '8px'}} onClick={() => {
          openFeedbackDialog();
          handleCloseDropdown();
        }}>
          <IconMoreAction />
          <Typography sx={{flexGrow: 1, fontSize: '14px'}}>{t('landing.submitFeedback')}</Typography>
        </MenuItem>}
      </CustomMenu>

      <CustomMenu
        dense
        open={learnMoreOpen}
        anchorEl={anchorRefLearnMore.current}
        onClose={handleCloseLearnMore}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'right',
          horizontal: 'top',
        }}
      >
        {dropDownBtnOptions.map((option, index) => (
          <CustomMenuItem
            sx={{
              'padding': 0,
              'color': 'black',
              'width': '100%',
              '&:hover': {
                color: 'white',
              },
            }}
            key={index}
          >
            <StyledLink
              onClick={() => openUrlInBrowser(option.to)}
            >
              <Typography sx={{fontSize: '0.875rem', lineHeight: '20px'}}>{t(option.name)}</Typography>
            </StyledLink>
          </CustomMenuItem>
        ))}
      </CustomMenu>
    </Box>
  );
}

